import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { StripeContext, StripeProvider } from './StripeContext';
import { UserBillingPageContext, UserBillingPageProvider } from './UserBillingPageContext';
import {
  BillingInfoDrawer,
  AddPaymentStripeDrawer,
  TopUpDrawer,
  BuyCreditsModal,
} from 'lib.shared';
import { BillingSectionType } from './models/billing-section-types';
import { EnableBoostProDrawer, ProRequiredModal } from 'lib.components';
import { usePostSubscriptionMutation } from 'lib.store/src/main/subscription/subscription.api';
import { usePostDefaultBillingMethodIdMutation } from 'lib.store/src/main/payment/payment.api';
import { useDispatch } from 'react-redux';
import type { RootState } from 'lib.store/src';
import { useAppSelector } from 'lib.hooks/src/redux';
import { message } from 'antd';
import sendAnalytics from 'lib.hooks/src/sendAnalytics/sendAnalytics';
import { GTMEventName } from 'lib.hooks/src/sendAnalytics/constants';
import { updateUserSubscriptionInStore } from 'lib.shared';
import { setProRequiredHandler } from 'lib.shared/src/handleApiError';
import { PostSubscriptionResponse } from 'lib.store/src/main/subscription/models';
type StripeValue = React.ContextType<typeof StripeContext>;
type UserBillingPageValue = React.ContextType<typeof UserBillingPageContext>;

interface BillingContextType {
  stripe: StripeValue;
  billing: UserBillingPageValue;
}

export const BillingContext = createContext<BillingContextType | undefined>(undefined);

interface BillingProviderProps {
  children: ReactNode;
}

const BillingContextBridge: React.FC<{ children: ReactNode }> = ({ children }) => {
  const stripe = useContext(StripeContext);
  const billing = useContext(UserBillingPageContext);
  const [onSubscribe] = usePostSubscriptionMutation();
  const [postDefault] = usePostDefaultBillingMethodIdMutation();
  const dispatch = useDispatch();
  const user = useAppSelector((state: RootState) => state.userAuth.user);
  const {
    actualSectionType,
    setActualSectionType,
    drawerForm,
    onFormChange,
    validationErrors,
    isLoading,
    setLoading,
    isUpdating,
    onBillingInfoUpdate,
    clickedSectionType,
    setClickedSectionType,
    isModalView,
    close,
    topUpAmount,
    actualDrawerTypeHandler,
  } = billing;

  const { selectedCardId } = useContext(StripeContext);

  // the launch paywall: showApiError opens it on the backend's "PRO required" refusal
  const [isProRequiredOpen, setIsProRequiredOpen] = useState(false);
  useEffect(() => setProRequiredHandler(() => setIsProRequiredOpen(true)), []);

  const value: BillingContextType = {
    stripe,
    billing,
  };

  const onBillingInfoUpdateClick = () => {
    if (
      clickedSectionType !== BillingSectionType.BILLING_INFO &&
      clickedSectionType !== BillingSectionType.ENABLE_PRO
    ) {
      return onBillingInfoUpdate('', () => {
        setActualSectionType(BillingSectionType.ADD_PAYMENT_METHOD);
      });
    }
    return onBillingInfoUpdate('', () => {
      setActualSectionType(BillingSectionType.NONE);
    });
  };

  const onEnablePro = async () => {
    if (!selectedCardId) {
      void message.open({
        type: 'error',
        content: 'Please select a payment method before enabling Boost PRO.',
      });
      return;
    }
    setLoading(true);
    try {
      const defaultResponse = await postDefault({ id: selectedCardId });
      if ('error' in defaultResponse) {
        void message.open({
          type: 'error',
          content:
            (defaultResponse as { error: { data?: { message?: string } } })?.error?.data?.message ||
            'Failed to mark the payment method as default.',
        });
        return;
      }

      const subscriptionResponse = await onSubscribe();
      if ('error' in subscriptionResponse) {
        void message.open({
          type: 'error',
          content:
            (subscriptionResponse as { error: { data: { message: string } } })?.error?.data
              ?.message || 'Subscription error',
        });
        return;
      }

      if (subscriptionResponse && 'data' in subscriptionResponse) {
        sendAnalytics(GTMEventName.enableProSubscription);
        void message.success('Boost PRO is on');
        updateUserSubscriptionInStore(
          dispatch,
          subscriptionResponse as unknown as { data: PostSubscriptionResponse },
          user,
        );
        setClickedSectionType(BillingSectionType.NONE);
        setActualSectionType(BillingSectionType.NONE);
      }
    } catch (error) {
      void message.open({
        type: 'error',
        content: 'Subscription error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <BillingContext.Provider value={value}>
      {children}

      {!isModalView && (
        <BillingInfoDrawer
          isOpen={actualSectionType === BillingSectionType.BILLING_INFO}
          onClose={close}
          form={drawerForm}
          onValuesChange={onFormChange}
          errors={validationErrors}
          isLoading={isLoading || isUpdating}
          onSubmit={onBillingInfoUpdateClick}
        />
      )}

      {!isModalView && (
        <AddPaymentStripeDrawer
          isOpen={actualSectionType === BillingSectionType.ADD_PAYMENT_METHOD}
          onClose={close}
          isLoading={isLoading || isUpdating}
          onSuccessCallback={() => {
            setActualSectionType(BillingSectionType.NONE);
          }}
        />
      )}
      {clickedSectionType === BillingSectionType.ENABLE_PRO && !actualSectionType ? (
        <EnableBoostProDrawer
          isOpen={clickedSectionType === BillingSectionType.ENABLE_PRO}
          onConfirm={() => void onEnablePro()}
          isLoading={isLoading}
          onClose={() => {
            setClickedSectionType(BillingSectionType.NONE);
            setActualSectionType(BillingSectionType.NONE);
          }}
        />
      ) : null}
      <ProRequiredModal
        isOpen={isProRequiredOpen}
        onClose={() => setIsProRequiredOpen(false)}
        onEnablePro={() => {
          setIsProRequiredOpen(false);
          actualDrawerTypeHandler({
            actionType: BillingSectionType.ENABLE_PRO,
            isModalView: false,
          });
        }}
      />
      <TopUpDrawer
        isOpen={
          clickedSectionType === BillingSectionType.TOP_UP &&
          actualSectionType === BillingSectionType.NONE
        }
        onClose={() => {
          setClickedSectionType(BillingSectionType.NONE);
          setActualSectionType(BillingSectionType.NONE);
        }}
        initialAmount={topUpAmount || undefined}
      />
      <BuyCreditsModal
        isOpen={
          clickedSectionType === BillingSectionType.BUY_CREDITS &&
          actualSectionType === BillingSectionType.NONE
        }
        onClose={() => {
          setClickedSectionType(BillingSectionType.NONE);
          setActualSectionType(BillingSectionType.NONE);
        }}
      />
    </BillingContext.Provider>
  );
};

export const BillingProvider: React.FC<BillingProviderProps> = ({ children }) => {
  return (
    <StripeProvider>
      <UserBillingPageProvider>
        <BillingContextBridge>{children}</BillingContextBridge>
      </UserBillingPageProvider>
    </StripeProvider>
  );
};

export const useBilling = (): BillingContextType => {
  const context = useContext(BillingContext);
  if (!context) {
    throw new Error('useBilling must be used within a BillingProvider');
  }
  return context;
};
